import { useState, useEffect } from 'react';
import { fetchCohortDetails } from '../services/api';
import { isIndiaTimezone } from '../utils/locale';

/**
 * Custom hook to fetch and cache cohort details.
 * Uses India cohort (REACT_APP_INDIA_COHORT_ID) when user's timezone is India, otherwise default cohort.
 * @returns {object} { cohortDetails, loading, error, isIndia }
 */
export const useCohortDetails = () => {
  const [cohortDetails, setCohortDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isIndia = isIndiaTimezone();
  const partnerId = process.env.REACT_APP_PARTNER_ID;
  const defaultCohortId = process.env.REACT_APP_COHORT_ID;
  const indiaCohortId = process.env.REACT_APP_INDIA_COHORT_ID;
  const cohortId = isIndia && indiaCohortId ? indiaCohortId : defaultCohortId;
  useEffect(() => {
    if (!partnerId || !cohortId) {
      console.warn('Cohort ID or Partner ID not configured');
      setLoading(false);
      return;
    }

    const cacheKey = `cohort_${cohortId}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          setCohortDetails(parsed.data);
          setLoading(false);
          return;
        }
      } catch (e) {
        // Invalid cache, continue to fetch
      }
    }

    fetchCohortDetails(partnerId, cohortId)
      .then(data => {
        setCohortDetails(data);
        setError(null);
        sessionStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
      })
      .catch(err => {
        console.error('Error fetching cohort details:', err);
        setError(err);
      })
      .finally(() => setLoading(false));
  }, [partnerId, cohortId]);

  return { cohortDetails, loading, error, isIndia };
};
